/**
 * Generates data-driven (formulaic) intro copy + FAQ items for the freelance hub's static
 * SEO header. Pure data-in -> text-out, same approach as
 * generate-category-market-header-text.ts, with "Popular fields" (from the same-query `field`
 * facet) standing in for that file's "related categories" section — freelance work doesn't
 * have a curated related-hub list, but does have a real field-facet breakdown for free.
 */

import type { FaqItem } from '../../types/faq-item';

export type SupportedLocale = 'en' | 'fi' | 'sv';

export interface FreelanceHeaderMetrics {
  locale: SupportedLocale;
  jobCount: number;
  topLocations: Array<{ location: string; count: number }>;
  topFields: Array<{ field: string; count: number }>;
  remotePercentage: number;
}

export interface FreelanceHeaderText {
  introParagraph: string;
  faqItems: FaqItem[];
}

function localeNumber(value: number, locale: SupportedLocale): string {
  const localeString = locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-US';
  return Number.isFinite(value) ? value.toLocaleString(localeString) : String(value);
}

function topLocationNames(metrics: FreelanceHeaderMetrics, limit = 3): string[] {
  return metrics.topLocations.slice(0, limit).map((l) => l.location.split(',')[0].trim());
}

function topFieldNames(metrics: FreelanceHeaderMetrics, limit = 3): string[] {
  return metrics.topFields.slice(0, limit).map((f) => f.field);
}

function joinList(items: string[], locale: SupportedLocale): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  const and = locale === 'fi' ? 'ja' : locale === 'sv' ? 'och' : 'and';
  if (items.length === 2) return `${items[0]} ${and} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

function buildIntroEn(metrics: FreelanceHeaderMetrics): string {
  const { jobCount } = metrics;
  const jobsText = jobCount > 0 ? `${localeNumber(jobCount, 'en')} open freelance job${jobCount === 1 ? '' : 's'}` : 'open freelance jobs';
  const fields = topFieldNames(metrics);
  const fieldsPart = fields.length > 0 ? ` Most freelance demand is in ${joinList(fields, 'en')}.` : '';
  const locations = topLocationNames(metrics);
  const locationsPart = locations.length > 0 ? ` Most postings are based in ${joinList(locations, 'en')}.` : '';
  const remotePart = metrics.remotePercentage > 0 ? ` ${metrics.remotePercentage}% offer remote or hybrid work.` : '';
  return `Browse ${jobsText} in Finland, updated daily from verified employer listings.${fieldsPart}${locationsPart}${remotePart}`;
}

function buildIntroFi(metrics: FreelanceHeaderMetrics): string {
  const { jobCount } = metrics;
  const jobsText = jobCount > 0 ? `${localeNumber(jobCount, 'fi')} avointa keikkatyöpaikkaa` : 'avoimia keikkatyöpaikkoja';
  const fields = topFieldNames(metrics);
  const fieldsPart = fields.length > 0 ? ` Eniten keikkatöitä on aloilla: ${joinList(fields, 'fi')}.` : '';
  const locations = topLocationNames(metrics);
  const locationsPart = locations.length > 0 ? ` Suosituimmat sijainnit: ${joinList(locations, 'fi')}.` : '';
  const remotePart = metrics.remotePercentage > 0 ? ` ${metrics.remotePercentage}% työpaikoista tarjoaa etä- tai hybridityötä.` : '';
  return `Selaa ${jobsText} Suomessa, päivittyy päivittäin.${fieldsPart}${locationsPart}${remotePart}`;
}

function buildIntroSv(metrics: FreelanceHeaderMetrics): string {
  const { jobCount } = metrics;
  const jobsText = jobCount > 0 ? `${localeNumber(jobCount, 'sv')} lediga frilansjobb` : 'lediga frilansjobb';
  const fields = topFieldNames(metrics);
  const fieldsPart = fields.length > 0 ? ` Mest frilansefterfrågan finns inom: ${joinList(fields, 'sv')}.` : '';
  const locations = topLocationNames(metrics);
  const locationsPart = locations.length > 0 ? ` Populäraste orterna: ${joinList(locations, 'sv')}.` : '';
  const remotePart = metrics.remotePercentage > 0 ? ` ${metrics.remotePercentage}% erbjuder distans- eller hybridarbete.` : '';
  return `Bläddra bland ${jobsText} i Finland, uppdateras dagligen.${fieldsPart}${locationsPart}${remotePart}`;
}

function buildFaqEn(metrics: FreelanceHeaderMetrics): FaqItem[] {
  const { jobCount } = metrics;
  const items: FaqItem[] = [];

  items.push({
    question: 'How many freelance jobs are available in Finland right now?',
    answer: jobCount > 0
      ? `There are currently ${localeNumber(jobCount, 'en')} open freelance job${jobCount === 1 ? '' : 's'} listed on JobCrawls.`
      : 'Open freelance positions are updated daily on JobCrawls.',
  });

  const fields = topFieldNames(metrics);
  if (fields.length > 0) {
    items.push({
      question: 'What fields have the most freelance work?',
      answer: `Freelance demand is currently highest in ${joinList(fields, 'en')}.`,
    });
  }

  const locations = topLocationNames(metrics);
  if (locations.length > 0) {
    items.push({
      question: 'Where are most freelance jobs located?',
      answer: `Most freelance jobs in Finland are based in ${joinList(locations, 'en')}.`,
    });
  }

  if (metrics.remotePercentage > 0) {
    items.push({
      question: 'What percentage of freelance jobs are remote?',
      answer: `${metrics.remotePercentage}% of freelance job listings offer remote or hybrid work arrangements.`,
    });
  }

  return items;
}

function buildFaqFi(metrics: FreelanceHeaderMetrics): FaqItem[] {
  const { jobCount } = metrics;
  const items: FaqItem[] = [];

  items.push({
    question: 'Kuinka monta keikkatyöpaikkaa Suomessa on avoinna juuri nyt?',
    answer: jobCount > 0
      ? `JobCrawlsissa on tällä hetkellä ${localeNumber(jobCount, 'fi')} avointa keikkatyöpaikkaa.`
      : 'Avoimet keikkatyöpaikat päivittyvät päivittäin JobCrawlsissa.',
  });

  const fields = topFieldNames(metrics);
  if (fields.length > 0) {
    items.push({
      question: 'Millä aloilla on eniten keikkatöitä?',
      answer: `Keikkatöiden kysyntä on tällä hetkellä suurinta näillä aloilla: ${joinList(fields, 'fi')}.`,
    });
  }

  const locations = topLocationNames(metrics);
  if (locations.length > 0) {
    items.push({
      question: 'Missä keikkatyöpaikkoja on eniten?',
      answer: `Suurin osa keikkatyöpaikoista sijaitsee: ${joinList(locations, 'fi')}.`,
    });
  }

  if (metrics.remotePercentage > 0) {
    items.push({
      question: 'Kuinka suuri osa keikkatyöpaikoista on etätyötä?',
      answer: `${metrics.remotePercentage}% keikkatyöpaikoista tarjoaa etä- tai hybridityömahdollisuuden.`,
    });
  }

  return items;
}

function buildFaqSv(metrics: FreelanceHeaderMetrics): FaqItem[] {
  const { jobCount } = metrics;
  const items: FaqItem[] = [];

  items.push({
    question: 'Hur många frilansjobb finns just nu i Finland?',
    answer: jobCount > 0
      ? `Det finns för närvarande ${localeNumber(jobCount, 'sv')} lediga frilansjobb på JobCrawls.`
      : 'Lediga frilansjobb uppdateras dagligen på JobCrawls.',
  });

  const fields = topFieldNames(metrics);
  if (fields.length > 0) {
    items.push({
      question: 'Inom vilka områden finns det mest frilansarbete?',
      answer: `Efterfrågan på frilansarbete är för närvarande störst inom: ${joinList(fields, 'sv')}.`,
    });
  }

  const locations = topLocationNames(metrics);
  if (locations.length > 0) {
    items.push({
      question: 'Var finns de flesta frilansjobben?',
      answer: `De flesta frilansjobben i Finland finns i: ${joinList(locations, 'sv')}.`,
    });
  }

  if (metrics.remotePercentage > 0) {
    items.push({
      question: 'Hur stor andel av frilansjobben är distansarbete?',
      answer: `${metrics.remotePercentage}% av frilansjobben erbjuder distans- eller hybridarbete.`,
    });
  }

  return items;
}

export function generateFreelanceMarketHeaderText(metrics: FreelanceHeaderMetrics): FreelanceHeaderText {
  switch (metrics.locale) {
    case 'fi':
      return { introParagraph: buildIntroFi(metrics), faqItems: buildFaqFi(metrics) };
    case 'sv':
      return { introParagraph: buildIntroSv(metrics), faqItems: buildFaqSv(metrics) };
    default:
      return { introParagraph: buildIntroEn(metrics), faqItems: buildFaqEn(metrics) };
  }
}
